import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Tag, Space, Modal, Descriptions, Typography, Tooltip, message } from 'antd';
import { EyeOutlined, SyncOutlined, PlusOutlined } from '@ant-design/icons';
import { Tabs } from 'antd';
import { manualPaymentService } from 'services/manualPaymentService';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';



const { Text } = Typography;

interface DemandPayment {
  id: number;
  amount: number;
  date: string;
  method: string;
  type: string;
  status: string;
  currency: any | null;
  description?: string;
  externalReference?: string | null;
  approver?: string;
  errorMessage?: string;
  agencyName?: string;
  user: {
    id: number;
    login: string;
  };
}


interface PaymentDetailsState {
  visible: boolean;
  paymentId: number | null;
  payment: DemandPayment | null;
  loading: boolean;
}

type TabStatus = 'ALL' | 'REQUESTED' | 'PENDING' | 'COMPLETED_AND_CONSUMED' | 'CANCELED';

const DemandPayment: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<DemandPayment[]>([]);
  const [activeTab, setActiveTab] = useState<TabStatus>('ALL');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsState>({ visible: false, paymentId: null, payment: null, loading: false });

  const history = useHistory();



  const fetchPayments = useCallback(async (status: TabStatus, page: number, size: number) => {
  try {
    setLoading(true);
    const data = await manualPaymentService.getAllDemandByAgency(page - 1, size);

    setPayments(data.content);
    setPagination({
      current: page,
      pageSize: data.size,
      total: data.totalElements,
    });

  } catch (error) {
    console.error('Error fetching manual payments:', error);
    message.error(t('agency.demandPayment.loadError'));
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchPayments(activeTab, pagination.current, pagination.pageSize);
  }, [activeTab, fetchPayments, pagination.current, pagination.pageSize]);

  const handleTableChange = (newPagination: any) => {
    fetchPayments(activeTab, newPagination.current, newPagination.pageSize);
  };

  const handleViewDetails = async (paymentId: number) => {
    setPaymentDetails({ visible: true, paymentId, payment: null, loading: true });
    try {
      const payment = payments.find(p => p.id === paymentId) || null;
      setPaymentDetails({ visible: true, paymentId, payment, loading: false });
    } catch (error) {
      console.error('Error fetching payment details:', error);
      message.error('Erreur lors du chargement des détails du paiement');
      setPaymentDetails(prev => ({ ...prev, loading: false }));
    }
  };

  const handleModalClose = () => {
    setPaymentDetails({ visible: false, paymentId: null, payment: null, loading: false });
  };

  const handleNewPayment = () => {
    history.push('/agency/manual-payment'); 
    // ou la route exacte de ton formulaire
  };


  const renderStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      PENDING: { color: 'blue', text: t('paymentStatus.PENDING') },
      INITIALED: { color: 'blue', text: t('paymentStatus.INITIALED') },
      REQUESTED: { color: 'cyan', text: t('paymentStatus.REQUESTED') },
      COMPLETED: { color: 'green', text: t('paymentStatus.COMPLETED') },
      COMPLETED_WITH_ERROR: { color: 'orange', text: t('paymentStatus.COMPLETED_WITH_ERROR') },
      COMPLETED_AND_CONSUMED: { color: 'green', text: t('paymentStatus.COMPLETED_AND_CONSUMED') },
      FAILED: { color: 'red', text: t('paymentStatus.FAILED') },
      ABORTED: { color: 'volcano', text: t('paymentStatus.ABORTED') },
      CANCELED: { color: 'volcano', text: t('paymentStatus.CANCELED') },
      REFUNDED: { color: 'green', text: t('paymentStatus.REFUNDED') },
      REFUND_REQUESTED: { color: 'cyan', text: t('paymentStatus.REFUND_REQUESTED') },
      REFUND_COMPLETED: { color: 'green', text: t('paymentStatus.REFUND_COMPLETED') },
      REFUND_FAILED: { color: 'red', text: t('paymentStatus.REFUND_FAILED') },
      PROCESSING: { color: 'orange', text: t('paymentStatus.PROCESSING') },
      ISSUE: { color: 'volcano', text: t('paymentStatus.ISSUE') }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const renderPaymentMethodTag = (method: string) => {
    const methodMap: Record<string, { color: string; text: string; }> = {
      BANK_TRANSFER: { color: 'blue', text: t('paymentMethod.BANK_TRANSFER')},
      CHECK: { color: 'purple', text: t('paymentMethod.CHECK')},
      CASH: { color: 'green', text: t('paymentMethod.CASH') },
      CARD: { color: 'red', text: t('paymentMethod.CARD') },
      OTHER: { color: 'orange', text: t('paymentMethod.OTHER') }
    };
    const config = methodMap[method] || { color: 'default', text: method };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


   const columns = [
    { 
      title: t('common.reference'), dataIndex: 'externalReference', key: 'externalReference',
       render: (text?: string) =>  text ? <Text strong>{text}</Text> : <Text type="secondary">---</Text>
    },
    { 
      title: t('common.amount'), dataIndex: 'amount', key: 'amount', 
      render: (amount: number) => <Text strong style={{ color: '#1890ff', fontSize: 16 }}>€{amount?.toFixed(2)}</Text>
    },
    { title: t('common.method'), dataIndex: 'method', key: 'method', render: renderPaymentMethodTag },
    { title: t('common.date'), dataIndex: 'date', key: 'date', render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm') },
    { title: t('common.status'), dataIndex: 'status', key: 'status', render: renderStatusTag },
    {
      title: t('common.actions'), key: 'actions',
      render: (_: any, record: DemandPayment) => (
        <Space>
          <Tooltip title={t('agency.demandPayment.viewDetails')}>
            <Button icon={<EyeOutlined style={{paddingLeft:'.3em'}} />} size="small" type="primary" onClick={() => handleViewDetails(record.id)} />
          </Tooltip>
          
      
        </Space>
      )
    }
  ];

  return (
    <Card
      title={<span style={{ fontSize: 20 }}>📋 {t('agency.demandPayment.title')}</span>}
      extra={
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNewPayment} size="large">{t('agency.demandPayment.newRequest')}</Button>
          <Button icon={<SyncOutlined />} onClick={() => fetchPayments(activeTab, pagination.current, pagination.pageSize)} loading={loading}>{t('common.refresh')}</Button>
        </Space>
      }
    >
      {/* <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
        {(['ALL', 'REQUESTED', 'PENDING', 'COMPLETED_AND_CONSUMED', 'CANCELED'] as TabStatus[]).map(status => {
          const labels: Record<TabStatus, string> = { ALL: 'Tous', REQUESTED: 'Demandés', PENDING: 'En attente', COMPLETED_AND_CONSUMED: 'Validés', CANCELED: 'Rejetés' };
          return (
            <TabPane
              tab={<Badge count={totalByStatus[status] || 0} showZero><span style={{ padding: '0 8px' }}>{labels[status]}</span></Badge>}
              key={status}
            > */}
              <Table
                columns={columns}
                dataSource={payments}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                locale={{ emptyText: t('agency.demandPayment.noRequests') }}
              />
            {/* </TabPane>
          );
        })} 
      </Tabs>*/}

   <Modal
  open={paymentDetails.visible}
  title={t('agency.demandPayment.detailsTitle')}
  onCancel={handleModalClose}
  footer={[
    <Button key="close" type="primary" onClick={handleModalClose} >
      {t('common.close')}
    </Button>
  ]}
  width={700}
>
  {paymentDetails.loading ? (
    <Text>{t('common.loading')}</Text>
  ) : paymentDetails.payment ? (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label={t('common.reference')}>
        {paymentDetails.payment.externalReference || '---'}
      </Descriptions.Item>

      <Descriptions.Item label={t('common.amount')}>
        €{paymentDetails.payment.amount.toFixed(2)}
      </Descriptions.Item>

      <Descriptions.Item label={t('common.method')}>
        {renderPaymentMethodTag(paymentDetails.payment.method)}
      </Descriptions.Item>

      <Descriptions.Item label={t('common.type')}>
        {paymentDetails.payment.type}
      </Descriptions.Item>

      <Descriptions.Item label={t('common.status')}>
        {renderStatusTag(paymentDetails.payment.status)}
      </Descriptions.Item>

      <Descriptions.Item label={t('common.date')}>
        {moment(paymentDetails.payment.date).format('DD/MM/YYYY HH:mm')}
      </Descriptions.Item>

   

      {paymentDetails.payment.description && (
        <Descriptions.Item label={t('common.description')}>
          {paymentDetails.payment.description}
        </Descriptions.Item>
      )}

      {paymentDetails.payment.errorMessage && (
        <Descriptions.Item label={t('common.errorMessage')}>
          <Text type="danger">
            {paymentDetails.payment.errorMessage}
          </Text>
        </Descriptions.Item>
      )}

      {paymentDetails.payment.approver && (
        <Descriptions.Item label={t('common.approvedBy')}>
          {paymentDetails.payment.approver}
        </Descriptions.Item>
      )}
    </Descriptions>
  ) : (
    <Text type="secondary">{t('common.noDetailsAvailable')}</Text>
  )}
</Modal>

    </Card>
  );
};

export default DemandPayment;
